import React from 'react';
import { Dialog } from 'primereact/dialog';

/**
 * @typedef {Object} ContentItem
 * @property {string} type - Type of content ('text', 'image', 'video', 'gif', 'link', 'custom')
 * @property {string} content - Main content text or URL
 * @property {string} [className] - Optional CSS class
 * @property {Object} [props] - Additional properties based on type
 */

/**
 * @typedef {Object} Update
 * @property {string} date - Date or title of the update
 * @property {ContentItem[]} items - Array of content items
 * @property {string} [className] - Optional CSS class for the update section
 */

const WhatsNew = ({ visible = true, onHide }) => {
    const updates = [
        {
            date: 'Nouveautés sur DeePsy – Comparaison des participants, restitutions améliorées !',
            style: { marginBottom: '2rem' },
            items: [
                {
                    type: 'text',
                    content: 'Bonjour,',
                    style: {
                        fontSize: '1.1rem',
                        marginBottom: '1.5rem'
                    }
                },
                {
                    type: 'text',
                    content: 'Nous avons récemment ajouté plusieurs nouvelles fonctionnalités sur DeePsy pour enrichir votre expérience sur le Dashboard.',
                    style: {
                        fontSize: '1rem',
                        lineHeight: '1.6',
                        marginBottom: '2rem'
                    }
                },
                {
                    type: 'custom',
                    content: 'Comparaison Participants / Métiers',
                    className: "has-text-dgrad-blue",
                    style: {
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        marginBottom: '1rem'
                    }
                },
                {
                    type: 'text',
                    content: 'Vous pouvez désormais comparer un participant avec d\'autres, qu\'ils soient de la même campagne ou non, ou encore avec une liste de plus de <strong>250 métiers</strong>.<br />Cette liste repose sur une étude menée par des chercheurs de l\'<strong>Université d\'Édimbourg</strong>, basée sur l\'analyse des traits de personnalité du Big Five sur un panel de <strong>68 000 personnes</strong>.<br /><small>À noter que cette fonctionnalité est encore en phase de "Bêta testing"</small>',
                    style: {
                        lineHeight: '1.6',
                        marginBottom: '1rem'
                    }
                },
                {
                    type: 'video',
                    content: '/changelog_files/videos/DeePsy_comparison_showcase.mp4',
                    props: {
                        controls: true,
                        autoPlay: true,
                        muted: true,
                        loop: true,
                    },
                    style: {
                        width: '100%',
                        borderRadius: '8px',
                        marginBottom: '2.5rem',
                        outline: '2px solid deepskyblue',
                    }
                },
                {
                    type: 'custom',
                    content: 'Un nouveau design pour les restitutions',
                    className: "has-text-dgrad-blue",
                    style: {
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        marginBottom: '1rem'
                    }
                },
                {
                    type: 'text',
                    content: 'Les rapports téléchargeables bénéficient d\'une refonte visuelle, avec un rafraîchissement des couleurs et une meilleure lisibilité.',
                    style: {
                        lineHeight: '1.6',
                        marginBottom: '1rem'
                    }
                },
                {
                    type: 'image',
                    content: '/changelog_files/images/changelog_gif_preview.png',
                    props: { alt: 'Aperçu PDF' },
                    style: {
                        width: '100%',
                        paddingBottom: '2.5rem',
                        borderRadius: 8,
                    }
                },
                {
                    type: 'custom',
                    content: 'Téléchargement groupé des restitutions',
                    className: "has-text-dgrad-blue",
                    style: {
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        marginBottom: '1rem'
                    }
                },
                {
                    type: 'text',
                    content: 'Vous pouvez désormais télécharger en un seul fichier PDF l\'ensemble des résultats des tests d\'un participant, pour un accès plus simple et rapide.',
                    style: {
                        lineHeight: '1.6',
                        marginBottom: '1rem'
                    }
                },
                {
                    type: 'gif',
                    content: '/changelog_files/gif/DeePsy_pdf_merge_showcase.gif',
                    props: {
                        controls: true,
                        autoplay: true,
                        muted: true,
                        loop: true,
                        alt: 'Nouveau design réstitution'
                    },
                    style: {
                        width: '100%',
                        borderRadius: '8px',
                        marginBottom: '3.5rem',
                        outline: '2px solid deepskyblue',
                    }
                },
                {
                    type: 'text',
                    content: 'Nous espérons que ces nouveautés amélioreront votre utilisation de DeePsy.',
                    style: {
                        lineHeight: '1.6',
                        marginBottom: '1.5rem'
                    }
                },
                {
                    type: 'text',
                    content: 'À très bientôt sur la plateforme !',
                    style: {
                        fontSize: '1.1rem',
                        marginTop: '2rem'
                    }
                },
                {
                    type: 'text',
                    content: 'L\'équipe DeePsy',
                    style: {
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                    }
                }
            ]
        }
    ];

    const renderContent = (item) => {
        switch (item.type) {
            case 'image':
                return <img src={item.content} {...item.props} style={item.style} />;
            case 'video':
                return <video {...item.props} style={item.style}>
                    <source src={item.content} type="video/mp4" />
                </video>;
            case 'gif':
                return <img src={item.content} {...item.props} style={item.style} />;
            case 'link':
                return <a {...item.props} style={item.style}>{item.content}</a>;
            case 'custom':
                return (
                    <div style={item.style}>
                        {item.props?.icon && <i className={item.props.icon}></i>}
                        <span>{item.content}</span>
                    </div>
                );
            default:
                return <div
                    style={item.style}
                    dangerouslySetInnerHTML={{ __html: item.content }}
                />;
        }
    };

    return (
        <Dialog
            header="Nouveautés"
            dismissableMask={true}
            visible={visible}
            onHide={onHide}
            style={{ width: '700' }}
            contentStyle={{ padding: '2rem' }}
            draggable={true}
            modal
        >
            <div style={{ maxWidth: '650px', margin: '0 auto' }}>
                {updates.map((update, index) => (
                    <div key={index} style={update.style}>
                        <h4
                            style={{
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                marginBottom: '2rem',
                                lineHeight: '1.4'
                            }}
                            className="has-text-dgrad-blue"
                        >
                            {update.date}
                        </h4>
                        <div>
                            {update.items.map((item, itemIndex) => (
                                <div key={itemIndex}>
                                    {renderContent(item)}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </Dialog>
    );
};

export default WhatsNew;